export const POLARITY = {
  HIGHER_IS_BETTER: 'higher_is_better',
  LOWER_IS_BETTER: 'lower_is_better',
};

export const DELTA_FORMAT = {
  PERCENTAGE_POINTS: 'percentage_points',
  ABSOLUTE: 'absolute',
  MULTIPLIER: 'multiplier',
  CURRENCY_WITH_MULTIPLIER: 'currency_with_multiplier',
};

export const PRODUCT_ENGAGEMENT_GROUP_ID = 'product_engagement';

export const INDUSTRY_COMPARISON_GROUPS = [
  {
    id: 'adoption_satisfaction',
    name: 'Adoption & Satisfaction',

    metrics: [
      {
        label: 'Enrollment Rate',
        metricId: 'enrollment_l30d',
        polarity: POLARITY.HIGHER_IS_BETTER,
        deltaFormat: DELTA_FORMAT.PERCENTAGE_POINTS,
        hardcodedFallback: () => ({
          value: 62,
          average: 54,
          maxValue: 100,
          formattedValue: '62.0%',
          formattedAverage: '54.0%',
          deltaFormatted: '+8.0%',
          deltaColorClass: 'delta-positive',
        }),
      },
      {
        label: 'Positive Employer Sentiment',
        metricId: 'employer_sentiment_positive_l30d',
        polarity: POLARITY.HIGHER_IS_BETTER,
        deltaFormat: DELTA_FORMAT.PERCENTAGE_POINTS,
        hardcodedFallback: () => ({
          value: 78,
          average: 71,
          maxValue: 100,
          formattedValue: '78.0%',
          formattedAverage: '71.0%',
          deltaFormatted: '+7.0%',
          deltaColorClass: 'delta-positive',
        }),
      },
      {
        label: 'New Joiner Activation Rate',
        metricId: 'new_joiner_conversion_l30d',
        polarity: POLARITY.HIGHER_IS_BETTER,
        deltaFormat: DELTA_FORMAT.PERCENTAGE_POINTS,
        hardcodedFallback: () => ({
          value: 68,
          average: 59,
          maxValue: 100,
          formattedValue: '68.0%',
          formattedAverage: '59.0%',
          deltaFormatted: '+9.0%',
          deltaColorClass: 'delta-positive',
        }),
      },
    ],
  },
  // Hidden due to data issues with non-enrolled/all tenure metrics
  // {
  //   name: 'Retention & Tenure',
  //   metrics: [
  //     { label: 'Average Employee Tenure', metricId: 'tenure_avg_months_all', ... },
  //     { label: '24-Month Retention (All)', metricId: 'tenure_over_24m_all', ... },
  //     { label: '12-Month Retention (All)', metricId: 'tenure_over_12m_all', ... },
  //   ],
  // },
  {
    id: PRODUCT_ENGAGEMENT_GROUP_ID,
    name: 'Product Engagement',

    metrics: [
      {
        label: 'Daily App Visitors',
        metricId: 'dau_from_enrolled',
        polarity: POLARITY.HIGHER_IS_BETTER,
        deltaFormat: DELTA_FORMAT.PERCENTAGE_POINTS,
        hardcodedFallback: () => ({
          value: 24,
          average: 19,
          maxValue: 100,
          formattedValue: '24.0%',
          formattedAverage: '19.0%',
          deltaFormatted: '+5.0%',
          deltaColorClass: 'delta-positive',
        }),
      },
      {
        label: 'Monthly App Visitors',
        metricId: 'mau_from_enrolled',
        polarity: POLARITY.HIGHER_IS_BETTER,
        deltaFormat: DELTA_FORMAT.PERCENTAGE_POINTS,
        hardcodedFallback: () => ({
          value: 71,
          average: 63,
          maxValue: 100,
          formattedValue: '71.0%',
          formattedAverage: '63.0%',
          deltaFormatted: '+8.0%',
          deltaColorClass: 'delta-positive',
        }),
      },
    ],
  },
  {
    id: 'financial_wellness',
    name: 'Financial Wellness',

    metrics: [
      {
        label: 'Avg Savings per Enrolled Employee',
        metricId: 'savings_balance_per_enrolled',
        polarity: POLARITY.HIGHER_IS_BETTER,
        deltaFormat: DELTA_FORMAT.CURRENCY_WITH_MULTIPLIER,
        hardcodedFallback: currencySymbol => ({
          value: 342,
          average: 215,
          maxValue: 410,
          formattedValue: `${currencySymbol}342.00`,
          formattedAverage: `${currencySymbol}215.00`,
          deltaFormatted: `+${currencySymbol}127.00 (1.6×)`,
          deltaColorClass: 'delta-positive',
        }),
      },
    ],
  },
  // Operational Efficiency group removed — internal operational metrics not suitable for client-facing impact view
];

// Hardcoded fallback for the page-visits sub-list inside the Product Engagement group.
// Used when portalHardcodedData2026Enabled is on and the real page-visit data is empty/null.
export const HARDCODED_PAGE_VISITS = [
  {
    id: 'page-visits-track-views',
    label: 'Track Views',
    description: null,
    tooltipId: 'ic-page-visits-track-views',
    value: 38,
    average: 31,
    maxValue: 100,
    formattedValue: '38.0%',
    formattedAverage: '31.0%',
    deltaFormatted: '+7.0%',
    deltaColorClass: 'delta-positive',
  },
  {
    id: 'page-visits-save-views',
    label: 'Save Views',
    description: null,
    tooltipId: 'ic-page-visits-save-views',
    value: 42,
    average: 35,
    maxValue: 100,
    formattedValue: '42.0%',
    formattedAverage: '35.0%',
    deltaFormatted: '+7.0%',
    deltaColorClass: 'delta-positive',
  },
  {
    id: 'page-visits-pay-views',
    label: 'Pay Views',
    description: null,
    tooltipId: 'ic-page-visits-pay-views',
    value: 64,
    average: 56,
    maxValue: 100,
    formattedValue: '64.0%',
    formattedAverage: '56.0%',
    deltaFormatted: '+8.0%',
    deltaColorClass: 'delta-positive',
  },
  {
    id: 'page-visits-hub-views',
    label: 'Hub Views',
    description: null,
    tooltipId: 'ic-page-visits-hub-views',
    value: 29,
    average: 24,
    maxValue: 100,
    formattedValue: '29.0%',
    formattedAverage: '24.0%',
    deltaFormatted: '+5.0%',
    deltaColorClass: 'delta-positive',
  },
];

// Hardcoded demo group appended to Compare tab when portalHardcodedData2026Enabled is on.
// Each metric is fully pre-formatted — bypasses the metricsMap lookup path.
export const HARDCODED_COMPARE_GROUP = {
  id: 'workforce_hardcoded',
  name: 'Workforce',
  metrics: [
    {
      id: 'avg_hourly_wage_yoy',
      label: 'Avg hourly wage (YoY)',
      description: 'Year-on-year growth in average hourly wage versus the sector average.',
      tooltipId: 'ic-hc-avg-hourly-wage-yoy',
      value: 4.2,
      average: 2.8,
      maxValue: 8,
      formattedValue: '+4.2%',
      formattedAverage: '+2.8%',
      deltaFormatted: '+1.4%',
      deltaColorClass: 'delta-positive',
      averageLabel: 'Sector avg',
    },
    {
      id: 'avg_hours_per_worker',
      label: 'Avg hours per worker per week',
      description: 'Average hours worked per shift worker per week, benchmarked against the sector.',
      tooltipId: 'ic-hc-avg-hours-per-worker',
      value: 32.4,
      average: 30.1,
      maxValue: 40,
      formattedValue: '32.4 hrs',
      formattedAverage: '30.1 hrs',
      deltaFormatted: '+2.3 hrs',
      deltaColorClass: 'delta-positive',
      averageLabel: 'Sector avg',
    },
  ],
};

// Hardcoded demo groups appended to Impact tab when portalHardcodedData2026Enabled is on.
// Own-trend only — "average" slot is repurposed as the 12-months-ago comparator.
// Factory takes the company currency symbol so the avg-salary strings render correctly for non-GBP tenants.
export const getHardcodedImpactGroups = currencySymbol => [
  {
    id: 'data_quality_hardcoded',
    name: 'Data Quality',
    metrics: [
      {
        id: 'shift_latency',
        label: 'Shift Latency',
        description: 'Time between an employee finishing a shift and that shift data being visible in Stream.',
        tooltipId: 'wi-hc-shift-latency',
        value: 2.4,
        average: 6.8,
        maxValue: 10,
        formattedValue: '2.4 hrs',
        formattedAverage: '6.8 hrs',
        deltaFormatted: '−4.4 hrs',
        deltaColorClass: 'delta-positive',
        averageLabel: '12 months ago',
      },
      {
        id: 'leaver_reporting',
        label: 'Leaver Reporting',
        description: 'Share of leavers whose termination data reaches Stream 10+ days after their last day.',
        tooltipId: 'wi-hc-leaver-reporting',
        value: 4.8,
        average: 14.2,
        maxValue: 25,
        formattedValue: '4.8%',
        formattedAverage: '14.2%',
        deltaFormatted: '−9.4%',
        deltaColorClass: 'delta-positive',
        averageLabel: '12 months ago',
      },
    ],
  },
  {
    id: 'workforce_health_hardcoded',
    name: 'Workforce Health',
    metrics: [
      {
        id: 'avg_salary',
        label: 'Avg salary',
        description: 'Average annual salary paid to your salaried workforce.',
        tooltipId: 'wi-hc-avg-salary',
        value: 36800,
        average: 34500,
        maxValue: 45000,
        formattedValue: `${currencySymbol}36,800`,
        formattedAverage: `${currencySymbol}34,500`,
        deltaFormatted: `+${currencySymbol}2,300`,
        deltaColorClass: 'delta-positive',
        averageLabel: '12 months ago',
      },
      {
        id: 'sickness_rate',
        label: 'Sickness rate',
        description: 'Rate of unplanned sickness absence as a percentage of scheduled hours.',
        tooltipId: 'wi-hc-sickness-rate',
        value: 2.8,
        average: 4.1,
        maxValue: 10,
        formattedValue: '2.8%',
        formattedAverage: '4.1%',
        deltaFormatted: '−1.3%',
        deltaColorClass: 'delta-positive',
        averageLabel: '12 months ago',
      },
    ],
  },
];

export const WORKFORCE_IMPACT_GROUPS = [
  {
    name: 'Retention & Tenure — The Core Impact Story',

    metrics: [
      {
        label: 'Employee Turnover (Monthly)',
        enrolledMetricId: 'enrolled_turnover_l30d',
        nonEnrolledMetricId: 'non_enrolled_turnover_l30d',
        polarity: POLARITY.LOWER_IS_BETTER,
        deltaFormat: DELTA_FORMAT.PERCENTAGE_POINTS,
      },
      // Hidden due to data issues with non-enrolled tenure metrics
      // { label: 'Average Employee Tenure', enrolledMetricId: 'tenure_avg_months_enrolled', nonEnrolledMetricId: 'tenure_avg_months_non_enrolled', ... },
      // { label: '24-Month Retention', enrolledMetricId: 'tenure_over_24m_enrolled', nonEnrolledMetricId: 'tenure_over_24m_non_enrolled', ... },
      // { label: '12-Month Retention', enrolledMetricId: 'tenure_over_12m_enrolled', nonEnrolledMetricId: 'tenure_over_12m_non_enrolled', ... },
      // { label: '6-Month Retention', enrolledMetricId: 'tenure_over_6m_enrolled', nonEnrolledMetricId: 'tenure_over_6m_non_enrolled', ... },
    ],
  },
  // Financial Wellness moved to INDUSTRY_COMPARISON_GROUPS — savings is a sector benchmark, not an enrolled-vs-non-enrolled comparison
];
